import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import DashboardMenu from "./DashboardMenu";
import SchoolNavContents from "./SchoolNavContents";
import UserMenu from "./UserMenu";
import NavDrawerButton from "../shared/NavDrawerButton";
import NavActionBar from "./NavActionBar";
import useNotificationsCount from "../../hooks/useNotificationsCount";
import useUnreadMessagesCount from "../../hooks/useUnreadMessagesCount";
import { useNightMode } from "../../contexts/NightModeContext";
import { isInactiveSchoolYear } from "../../shared/schoolYear";

// Where the "return to where I was" url lives. Kept in sessionStorage (not React
// state) because the navbar is remounted across full-document loads as it is
// bridged onto legacy pages, and separate React roots cannot share state. Per
// tab; the last non-Messages/Notifications page the user visited.
const DASHBOARD_RETURN_STORAGE_KEY = "schoolDashboardReturnUrl";

const readDashboardReturnUrl = () => {
  try {
    return window.sessionStorage.getItem(DASHBOARD_RETURN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

// Desktop-only preference: pin the action bar to the bottom of the viewport
// (floating, like mobile) instead of inline in the navbar center. Persisted in
// localStorage so it survives SPA navigations and full reloads, mirroring the
// night-mode idiom. Mobile always floats regardless of this flag. The toggle and
// the bar render in the same navbar root, so no cross-root reload is needed here.
const ACTION_BAR_BOTTOM_STORAGE_KEY = "v2_school_action_bar_bottom";

const readActionBarBottom = () => {
  try {
    return window.localStorage.getItem(ACTION_BAR_BOTTOM_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export default function SchoolNavbar({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode,
  linkComponent = "a",
  linkProps = {},
  leftExtras = null,
  rightExtras = null,
  loadingSlot = null,
  showNavDrawer = true,
  dashboardOpen: dashboardOpenProp,
  dashboardManage = false,
  onDashboardOpenChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { night } = useNightMode();
  // Where the action bar sits: mobile always floats at the bottom; on desktop the
  // user can pin it there via the hover handle, otherwise it stays inline in the
  // navbar center. The handle only shows on desktop (mobile has no equivalent
  // "top" slot to return to).
  const [actionBarBottom, setActionBarBottom] = useState(readActionBarBottom);
  const floatBottom = isMobile || actionBarBottom;
  const toggleActionBarBottom = () => {
    setActionBarBottom((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(
          ACTION_BAR_BOTTOM_STORAGE_KEY,
          next ? "1" : "0"
        );
      } catch {
        // storage unavailable (private mode / disabled): keep the toggle in memory.
      }
      return next;
    });
  };
  // Viewing a school year that is not the school's active one. The navbar is the
  // only place this is surfaced, so it outranks night mode: a warning that a
  // colour preference could suppress is not a warning.
  const inactiveYear = isInactiveSchoolYear(appProps, schoolYearId);
  // Night mode paints the navbar in the footer colour and flips its content
  // (icons + text) to white; day keeps the default light chrome. The orange
  // inactive-year wash needs the same white content as night for contrast.
  const lightOnDark = night || inactiveYear;
  const contentColor = lightOnDark ? "#FFFFFF" : "#000000";
  const iconColor = lightOnDark ? "#FFFFFF" : "#00000080";
  const [internalDashboardOpen, setInternalDashboardOpen] = useState(false);
  const isDashboardControlled = dashboardOpenProp !== undefined;
  const dashboardOpen = isDashboardControlled
    ? dashboardOpenProp
    : internalDashboardOpen;
  const setDashboardOpen = (next) => {
    if (!isDashboardControlled) setInternalDashboardOpen(next);
    onDashboardOpenChange?.(next);
  };

  // Cmd+K (Mac) / Ctrl+K (Windows/Linux) toggles the Dashboard launchpad from
  // anywhere. A ref mirrors the current open state so the listener reads fresh
  // state without re-subscribing on every toggle. The combo uses a modifier, so
  // it intentionally fires even while focus is in an input (command-palette
  // convention); preventDefault stops the browser's search/address-bar default.
  const dashboardOpenRef = useRef(dashboardOpen);
  dashboardOpenRef.current = dashboardOpen;
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;
      const isCmdK =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === "k";
      if (!isCmdK) return;
      e.preventDefault();
      setDashboardOpen(!dashboardOpenRef.current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const schoolName = appProps?.school?.name ?? "";
  const notificationsCount = useNotificationsCount(
    appProps,
    "Staff",
    schoolSlug
  );
  const messagesCount = useUnreadMessagesCount(appProps, schoolSlug);
  const navigate = useNavigate();

  // Messages/Notifications are v2 destinations. Inside the SPA (navigationMode
  // undefined) navigate client-side; when this same navbar is bridged onto a
  // legacy page (navigationMode "browser", a MemoryRouter decoupled from the
  // address bar) do a full-document load to cross into the SPA. Mirrors
  // SchoolNavMenu.handleNavigate, and covers the desktop icons, the mobile
  // UserMenu items (via onNavigate), and the bridged copy in one place.
  // Navigate to an absolute SPA url, honouring the bridge: a full-document load
  // when this navbar is mounted on a legacy page (MemoryRouter), a client-side
  // navigation inside the SPA.
  const goToUrl = (url) => {
    if (navigationMode === "browser") window.location.assign(url);
    else navigate(url);
  };
  const goTo = (path) => {
    if (!schoolSlug) return;
    goToUrl(`/v2/school/${schoolSlug}${path}`);
  };

  // Action bar (Notifications, Dashboard, Messages). Rendered once and placed
  // in the navbar's center slot: inline on desktop, floated to the bottom of
  // the viewport on mobile via the wrapper's fixed positioning. It rides along
  // with this navbar so it shows on both the SPA and the legacy pages this
  // chrome is bridged onto. Tabs: Notifications (0), Dashboard (1, the launchpad
  // overlay), Messages (2). The indicator tracks the current route; on bridged
  // legacy pages the real path lives on window.location, not the decoupled
  // MemoryRouter location.
  const location = useLocation();
  const currentPath =
    navigationMode === "browser" ? window.location.pathname : location.pathname;
  const onMessagesOrNotifications =
    currentPath.includes("/notifications") || currentPath.includes("/messages");
  const getBottomNavIndex = () => {
    if (currentPath.includes("/notifications")) return 0;
    if (currentPath.includes("/messages")) return 2;
    return 1;
  };

  // Remember the last page that is NOT Messages/Notifications so the Dashboard
  // icon can jump back to it. Stored as a root-relative url (path + query + hash)
  // so a v2 target can be restored with a client-side navigate. Only the SPA
  // records here: on bridged legacy pages v1's own router owns the recording (it
  // sees the client-side navigations this decoupled navbar root never re-renders
  // for - see app/javascript/views/ScrollToTop.js).
  useEffect(() => {
    if (navigationMode === "browser") return;
    if (onMessagesOrNotifications) return;
    try {
      window.sessionStorage.setItem(
        DASHBOARD_RETURN_STORAGE_KEY,
        window.location.pathname + window.location.search + window.location.hash
      );
    } catch {
      // sessionStorage unavailable (private mode / disabled): skip silently.
    }
  }, [currentPath, onMessagesOrNotifications, navigationMode]);

  const handleBottomNav = (index) => {
    if (index === 0) goTo("/notifications");
    else if (index === 2) goTo("/messages/inbox");
    else {
      // Dashboard tab, short press. On Messages/Notifications it returns the user
      // to the page they came from (press-and-hold opens the launchpad instead -
      // see handleActionBarLongPress). Everywhere else it opens the launchpad.
      const returnUrl = onMessagesOrNotifications
        ? readDashboardReturnUrl()
        : null;
      if (!returnUrl) {
        setDashboardOpen(true);
      } else if (returnUrl.startsWith("/v2/") && navigationMode !== "browser") {
        // Same v2 SPA document: navigate client-side, no reload.
        navigate(returnUrl);
      } else {
        // A v1 (or other) target lives in a different bundle; crossing needs a
        // full document load.
        window.location.assign(returnUrl);
      }
    }
  };

  // Press-and-hold on the Dashboard tab always opens the launchpad; returning
  // true swallows the trailing click so it does not also jump back. Holds on the
  // other tabs are left to behave as a normal tap.
  const handleActionBarLongPress = (index) => {
    if (index !== 1) return false;
    setDashboardOpen(true);
    return true;
  };
  // Live counters, index-matched to the bar's tabs: Notifications (0),
  // Dashboard (1, no badge), Messages (2).
  const bottomNavBadges = [notificationsCount, 0, messagesCount];

  // The reposition handle is desktop-only; it points down to send the inline bar
  // to the bottom, or up to bring the floating bar back inline.
  let repositionArrow = null;
  if (!isMobile) repositionArrow = actionBarBottom ? "up" : "down";

  // The hamburger + its drawer, on the far left of the navbar (mobile and
  // desktop). On mobile it takes the place of the school hat icon; the floating
  // action bar still carries the primary actions. Hidden in sidebar mode
  // (showNavDrawer false), where the permanent sidebar holds the nav contents.
  const navDrawerButton = showNavDrawer ? (
    <NavDrawerButton
      ariaLabel="Open navigation menu"
      anchor="left"
      iconColor={iconColor}
    >
      <SchoolNavContents
        appProps={appProps}
        schoolSlug={schoolSlug}
        schoolYearId={schoolYearId}
        navigationMode={navigationMode}
      />
    </NavDrawerButton>
  ) : null;

  return (
    <Box
      sx={{
        backgroundColor: (() => {
          if (inactiveYear) return theme.custom.layoutBg.inactiveYear;
          return night ? "#263238" : theme.custom.layoutBg.chrome;
        })(),
        // The navbar scrolls away with the page; the PageHeader is the sticky
        // top chrome now (see shared/components/PageHeader.jsx).
        position: "static",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          // Desktop keeps a tight vertical padding around the inline action bar;
          // mobile keeps the larger padding for the icon-only top bar.
          py: isMobile ? 1 : 0.5,
          px: 2,
          gap: 2,
        }}
      >
        {/* Left */}
        <Box
          sx={{
            // Desktop balances a fixed 40% against the right column so the
            // inline action bar stays centered. On mobile the action bar floats
            // out of flow, so the left side grows to fill the row and the name
            // clips right before the right-side account icon.
            width: isMobile ? "auto" : "40%",
            flex: isMobile ? 1 : "none",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 1 : 2,
            fontSize: 22,
            color: contentColor,
            "& a": { textDecoration: "none" },
          }}
        >
          {/* The hamburger (drawer trigger) leads the row on both mobile and
              desktop. Desktop then shows the school hat icon beside the name;
              mobile drops the hat since the hamburger already leads. The school
              year select follows on desktop only. (Fullscreen pages omit this
              whole navbar on mobile.) */}
          {navDrawerButton}
          <Box
            component={linkComponent}
            {...linkProps}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none",
              // minWidth: 0 lets this flex item shrink past its content so
              // the name ellipsis-clips instead of overflowing into the
              // right-side menu on narrow screens.
              minWidth: 0,
            }}
          >
            {!isMobile && (
              <SchoolIcon sx={{ color: iconColor, flexShrink: 0 }} />
            )}
            <Box
              component="span"
              sx={{
                color: contentColor,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                maxWidth: isMobile ? "none" : "250px",
              }}
            >
              {schoolName}
            </Box>
          </Box>
          {!isMobile && leftExtras}
        </Box>

        {/* Center: the action bar lives in the navbar center. On desktop it is
            inline and centered; on mobile the wrapper switches to fixed
            positioning so the bar floats at the bottom of the viewport. */}
        <Box
          sx={
            floatBottom
              ? {
                  position: "fixed",
                  left: "50%",
                  transform: "translateX(-50%)",
                  bottom: "calc(16px + env(safe-area-inset-bottom))",
                  zIndex: 1200,
                }
              : {
                  flexShrink: 0,
                  // Inline, the down handle drops below the bar into the region
                  // the sticky PageHeader (zIndex 1000) occupies. The bar's own
                  // backdrop-filter creates a stacking context the handle cannot
                  // escape on its own, so lift the whole wrapper above the header
                  // here; otherwise the handle renders behind it and is unclickable.
                  position: "relative",
                  zIndex: 1001,
                }
          }
        >
          <NavActionBar
            value={getBottomNavIndex()}
            onChange={handleBottomNav}
            onLongPress={handleActionBarLongPress}
            badges={bottomNavBadges}
            floating={floatBottom}
            compact={!isMobile}
            repositionArrow={repositionArrow}
            onReposition={toggleActionBarBottom}
          />
        </Box>
        <DashboardMenu
          open={dashboardOpen}
          onClose={() => setDashboardOpen(false)}
          initialManage={dashboardManage}
          appProps={appProps}
          schoolSlug={schoolSlug}
          schoolYearId={schoolYearId}
          navigationMode={navigationMode}
        />

        {/* Right */}
        <Box
          sx={{
            // Desktop holds 40% to balance the centered action bar; mobile only
            // needs the account icon's width so the left name can run up to it.
            width: isMobile ? "auto" : "40%",
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* The wash is colour-only, so this caption carries the meaning for
              screen readers and for anyone who cannot rely on the hue. It sits
              on the right rather than beside the year select because the left
              cluster is already tight - putting it there clipped the school name
              down to a few characters. The right side otherwise holds only the
              account menu. */}
          {inactiveYear && (
            <Typography
              variant="caption"
              sx={{
                color: contentColor,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                textAlign: "right",
                lineHeight: 1.2,
              }}
            >
              Viewing inactive year
            </Typography>
          )}
          {rightExtras}
          {/* Messages + Notifications now live in the center action bar, so the
              right side is just the account menu. */}
          <UserMenu staff={appProps?.staff} iconColor={iconColor} />
        </Box>
      </Box>
      {loadingSlot}
    </Box>
  );
}
